import React from "react";

import Api, { Activity } from "@/api";
import { PageModule } from "@/routes/types";

import * as Pages from "@/components/Pages";
import * as Companies from "@/models/companies";
import * as People from "@/models/people";
import * as Spaces from "@/models/spaces";

export default { name: "HomePage", loader, Page } as PageModule;

import { useMe } from "@/contexts/CurrentCompanyContext";
import { Feed, useItemsQuery } from "@/features/Feed";
import { useReloadFeedSignal } from "@/signals";
import { includesId } from "@/routes/paths";
import { showErrorToast } from "turboui";
import { canDeleteFeedItems } from "./feedPermissions";
import { Onboarding } from "./Onboarding";
import { SpacesZeroState } from "./SpacesZeroState";
import { BlockersPanel } from "./BlockersPanel";

interface LoaderData {
  company: Companies.Company;
  spaces: Spaces.Space[];
  adminIds: string[];
  ownerIds: string[];
}

async function loader(): Promise<LoaderData> {
  const company = await Companies.getCompany({
    includeOwners: true,
    includeAdmins: true,
    includePermissions: true,
  }).then((d) => d.company);

  const spaces = await Spaces.getSpaces({ includeAccessLevels: true });
  const adminIds = company.admins?.map((a) => a.id);
  const ownerIds = company.owners?.map((o) => o.id);

  return {
    company,
    spaces,
    adminIds: adminIds || [],
    ownerIds: ownerIds || [],
  };
}

function useLoadedData(): LoaderData {
  return Pages.useLoadedData() as LoaderData;
}

//
// Home / lobby — "feed canvas" direction.
//
// The spaces list now lives in the persistent quick-switch rail
// (SpacesRailLayout), so the home canvas leads with "What's new": the greeting
// plus the company activity feed, in view the moment you land.
//
function Page() {
  const { company, spaces } = useLoadedData();
  const isOwner = useIsOwner();
  const shouldPromptOnboarding = isOwner && !company.setupCompleted;

  return (
    <Pages.Page title="Home" testId="company-home">
      {shouldPromptOnboarding && <Onboarding company={company} />}

      <div className="flex gap-8 px-5 sm:px-8 lg:px-10 py-8 w-full max-w-6xl">
        <div className="flex-1 min-w-0">
          <Greeting spacesCount={spaces.length} />

          {spaces.length === 0 ? (
            <div className="mt-8">
              <SpacesZeroState />
            </div>
          ) : (
            <div className="mt-8">
              <ActivityFeed />
            </div>
          )}
        </div>

        {spaces.length > 0 && (
          <aside className="hidden xl:block w-80 shrink-0" data-test-id="home-right-rail">
            <div className="sticky top-8">
              <BlockersPanel companyId={company.id!} />
            </div>
          </aside>
        )}
      </div>
    </Pages.Page>
  );
}

function ActivityFeed() {
  const { company } = useLoadedData();
  const { data, loading, error, refetch } = useItemsQuery("company", company.id!);
  // Stable callback + memoized payload so the websocket subscription joins ONCE and
  // holds; a fresh payload object each render makes useSubscription leave/rejoin every
  // render and drop the live broadcasts (this feed was silently not updating).
  const onActivity = React.useCallback(() => refetch(), [refetch]);
  const feedPayload = React.useMemo(() => ({ resourceId: company.id! }), [company.id]);
  useReloadFeedSignal(onActivity, feedPayload);
  const canDeleteFeedItems = useCanDeleteFeedItems();
  const [deleteActivity] = Api.companies.useDeleteActivity();
  const [activities, setActivities] = React.useState(data?.activities || []);

  React.useEffect(() => {
    setActivities(data?.activities || []);
  }, [data?.activities]);

  const handleDeleteActivity = async (activity: Activity) => {
    if (!activity.id) return;

    try {
      await deleteActivity({ activityId: activity.id });
      setActivities((activities) => activities.filter((item) => item.id !== activity.id));
    } catch {
      showErrorToast("Could not delete feed item", "Please try again.");
    }
  };

  if (loading) return <ActivityFeedSkeleton />;
  if (error) return <div>Error</div>;

  return (
    <Feed
      items={activities}
      testId="company-feed"
      page="company"
      hideTopBorder
      paddedGroups
      canDeleteItems={canDeleteFeedItems}
      onDeleteItem={handleDeleteActivity}
    />
  );
}

function ActivityFeedSkeleton() {
  return (
    <div className="w-full">
      {/* Simulate 2 activity groups */}
      <ActivityGroupSkeleton />
      <ActivityGroupSkeleton />
    </div>
  );
}

function ActivityGroupSkeleton() {
  return (
    <div className="w-full border-t border-stroke-base animate-pulse flex flex-col sm:flex-row items-start gap-2 py-4">
      {/* Date section skeleton */}
      <div className="w-1/5 shrink-0 mb-2">
        <div className="h-4 bg-surface-dimmed rounded animate-pulse mb-1"></div>
        <div className="h-3 bg-surface-dimmed rounded animate-pulse w-3/4"></div>
      </div>

      {/* Activity items skeleton */}
      <div className="flex-1 flex flex-col gap-4">
        <ActivityItemSkeleton />
        <ActivityItemSkeleton />
        <ActivityItemSkeleton />
      </div>
    </div>
  );
}

function ActivityItemSkeleton() {
  return (
    <div className="flex flex-1 gap-3">
      {/* Avatar skeleton */}
      <div className="w-8 h-8 bg-surface-dimmed rounded-full animate-pulse"></div>

      {/* Content skeleton */}
      <div className="w-full break-words -mt-0.5">
        <div className="h-4 bg-surface-dimmed rounded animate-pulse mb-1 w-3/4"></div>
        <div className="h-3 bg-surface-dimmed rounded animate-pulse w-1/2"></div>
      </div>
    </div>
  );
}

function Greeting({ spacesCount }: { spacesCount: number }) {
  const me = useMe();

  let hour = new Date().getHours();
  let greeting = "";

  if (hour < 12) {
    greeting = "Good morning";
  } else if (hour < 18) {
    greeting = "Good afternoon";
  } else {
    greeting = "Good evening";
  }

  return (
    <div>
      <h1 className="font-bold text-2xl sm:text-3xl">
        {greeting}, {People.firstName(me!)}
      </h1>
      <p className="text-content-dimmed mt-1">
        Here's what moved across your {spacesCount} {spacesCount === 1 ? "space" : "spaces"}.
      </p>
    </div>
  );
}

function useIsOwner() {
  const { ownerIds } = useLoadedData();

  const me = useMe();
  return includesId(ownerIds, me!.id);
}

function useCanDeleteFeedItems() {
  const { adminIds, ownerIds } = useLoadedData();

  const me = useMe();
  return canDeleteFeedItems({ personId: me?.id, adminIds, ownerIds });
}
